/**
 * Branches.js — unified branches page.
 *
 * Works in two modes determined by URL params:
 *   /brands/:brandId/branches  → single-brand mode  (brand dropdown to switch)
 *   /users/:userId/branches    → all-user mode       (all brands grouped, admin only)
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import BrandNav from '../../components/common/BrandNav';
import { useAuth } from '../../context/AuthContext';

// ── Icons ─────────────────────────────────────────────────────────────────────
const IcoTV = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);
const IcoPhone = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 015.19 12.9a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
  </svg>
);
const IcoWhatsApp = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
const IcoPin = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IcoEdit = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IcoCopy = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
  </svg>
);
const IcoTrash = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

// ── TV Modal ──────────────────────────────────────────────────────────────────
function TVModal({ branch, onClose }) {
  const [screens, setScreens]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({ screen_label: '', orientation: 'H' });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/menuboard/screens?branch_id=${branch.id}`); setScreens(r.data); }
    catch { toast.error('Failed to load TVs'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [branch.id]); // eslint-disable-line

  const openAdd  = () => { setEditing(null); setForm({ screen_label: '', orientation: 'H' }); setShowForm(true); };
  const openEdit = s => { setEditing(s); setForm({ screen_label: s.screen_label, orientation: s.orientation }); setShowForm(true); };

  const save = async e => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/menuboard/screens/${editing.id}`, form);
      else         await api.post('/menuboard/screens', { ...form, branch_id: branch.id });
      toast.success(editing ? 'Updated!' : 'TV added!');
      setShowForm(false); load();
    } catch { toast.error('Error saving TV'); }
  };

  const del = async id => {
    if (!window.confirm('Delete this TV and all its slides?')) return;
    await api.delete(`/menuboard/screens/${id}`);
    toast.success('Deleted'); load();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500, width: '95vw' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">📺 TVs — {branch.name}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {showForm ? (
          <form onSubmit={save} style={{ paddingBottom: 16 }}>
            <div className="input-group">
              <label>TV Name</label>
              <input className="input" autoFocus required value={form.screen_label}
                onChange={e => setForm(f => ({ ...f, screen_label: e.target.value }))} placeholder="e.g. screen-h1" />
            </div>
            <div className="input-group">
              <label>Direction</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {[{ v:'H', label:'⟺ Horizontal', sub:'1920×1080', col:'#1565C0', bg:'#E3F2FD' },
                  { v:'V', label:'⇕ Vertical',   sub:'1080×1920', col:'#7B1FA2', bg:'#F3E5F5' }].map(o => (
                  <button key={o.v} type="button" onClick={() => setForm(f => ({ ...f, orientation: o.v }))}
                    style={{ flex:1, padding:'10px 8px', borderRadius:10, cursor:'pointer',
                      border:`2px solid ${form.orientation===o.v ? o.col : '#E0E0E0'}`,
                      background: form.orientation===o.v ? o.bg : '#fff' }}>
                    <div style={{ fontWeight:700, color: form.orientation===o.v ? o.col : '#555' }}>{o.label}</div>
                    <div style={{ fontSize:11, color:'#999', marginTop:2 }}>{o.sub}</div>
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:4 }}>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ flex:1, padding:9, borderRadius:8, border:'1.5px solid #E0E0E0', background:'#fff', cursor:'pointer', fontSize:14 }}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ flex:2, justifyContent:'center' }}>
                {editing ? 'Save Changes' : 'Add TV'}
              </button>
            </div>
          </form>
        ) : (
          <button onClick={openAdd} className="btn btn-primary" style={{ width:'100%', justifyContent:'center', marginBottom:14 }}>+ Add TV</button>
        )}

        {loading ? <div className="spinner" style={{ margin:'20px auto' }} /> : (
          <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:'50vh', overflowY:'auto' }}>
            {screens.length === 0 && !showForm && <div style={{ textAlign:'center', color:'#999', padding:'20px 0', fontSize:14 }}>No TVs yet</div>}
            {screens.map(s => {
              const isH = s.orientation === 'H';
              const col = isH ? '#1565C0' : '#7B1FA2'; const bg = isH ? '#E3F2FD' : '#F3E5F5';
              return (
                <div key={s.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10, border:'1.5px solid #EBEBEB', background:'#FAFAFA' }}>
                  <div style={{ width:34, height:34, borderRadius:8, background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:18 }}>📺</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:14 }}>{s.screen_label}</div>
                    <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:20, background:bg, color:col }}>{isH?'⟺ H':'⇕ V'}</span>
                  </div>
                  <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                    <button onClick={() => openEdit(s)} style={{ width:28, height:28, borderRadius:6, border:'1.5px solid #E0E0E0', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#555' }}><IcoEdit /></button>
                    <button onClick={() => del(s.id)} style={{ width:28, height:28, borderRadius:6, border:'1.5px solid #FFCDD2', background:'#FFF5F5', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#C62828' }}><IcoTrash /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Branch Card ───────────────────────────────────────────────────────────────
function BranchCard({ b, onEdit, onDuplicate, onDelete, onTVs, onMenu }) {
  const [hovered, setHovered] = useState(false);
  // "TVs (X)" should reflect ATTACHED DEVICES, not menuboard_screens rows.
  // device_count comes from the new devices.branch_id FK.
  const totalTVs = b.device_count ?? ((b.screens_h || 0) + (b.screens_v || 0));
  // TVs button is meaningful only when this branch IS a signage branch AND has at
  // least one device. QR-only / POS-only / empty signage branches → button disabled.
  const branchKinds = Array.isArray(b.kinds) ? b.kinds : (b.kind ? [b.kind] : ['signage']);
  const isSignage = branchKinds.includes('signage');
  const tvsClickable = isSignage && totalTVs > 0;
  // Menu button is meaningful only if the brand has a menu (at least one
  // category). Otherwise the editor would open empty — disable to make it clear.
  const menuClickable = b.brand_has_menu === true;

  const pill = {
    display:'flex', alignItems:'center', justifyContent:'center',
    gap:4, flex:1, height:30, borderRadius:8, fontSize:11, fontWeight:600,
    border:'1.5px solid #EBEBEB', background:'#FAFAFA',
    cursor:'pointer', textDecoration:'none', transition:'all 0.15s', whiteSpace:'nowrap',
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background:'#fff',
        // Use individual border-* properties so the green left strip survives
        // a hover state that only changes the other three borders.
        borderTop:    `1.5px solid ${hovered ? '#86EFAC' : '#EBEBEB'}`,
        borderRight:  `1.5px solid ${hovered ? '#86EFAC' : '#EBEBEB'}`,
        borderBottom: `1.5px solid ${hovered ? '#86EFAC' : '#EBEBEB'}`,
        borderLeft:   `5px solid ${hovered ? '#16A34A' : '#22C55E'}`,
        borderRadius:16,
        boxShadow: hovered ? '0 6px 24px rgba(0,0,0,0.10)' : '0 1px 6px rgba(0,0,0,0.06)',
        transition:'box-shadow 0.2s, border-color 0.2s',
        display:'flex', flexDirection:'column', overflow:'hidden',
      }}
    >
      {/* Header: logo + name on LEFT, kind flags pushed to the RIGHT */}
      <div style={{ padding:'12px 14px 8px', display:'flex', gap:10, alignItems:'center' }}>
        {/* Brand logo (replaces the pin emoji avatar). Falls back to the
            green pin tile when the brand has no logo set. */}
        {b.brand_logo ? (
          <img src={b.brand_logo} alt={b.brand_name || ''} style={{
            width:42, height:42, borderRadius:11, flexShrink:0,
            objectFit:'contain', background:'#fff',
            border:'1.5px solid #E0E0E0', padding:2,
          }} />
        ) : (
          <div style={{
            width:42, height:42, borderRadius:11, flexShrink:0,
            background:'linear-gradient(135deg,#DCFCE7,#BBF7D0)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:20, border:'1.5px solid #86EFAC',
          }}>📍</div>
        )}

        {/* Name block — takes remaining space (left). */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:14, color:'#111', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {b.name}
          </div>
          {b.name_ar && (
            <div style={{ fontFamily:'Cairo', fontSize:11, color:'#888', marginTop:1, direction:'rtl', textAlign:'left' }}>
              {b.name_ar}
            </div>
          )}
        </div>

        {/* Kind flags + Maps — pushed to the RIGHT edge of the card */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
          <div style={{ display:'flex', gap:4, flexWrap:'wrap', justifyContent:'flex-end' }}>
            {(() => {
              const kinds = Array.isArray(b.kinds) ? b.kinds : (b.kind ? [b.kind] : ['signage']);
              const KMETA = {
                signage:{ emoji:'📺', label:'TV',  bg:'#E0F2FE', border:'#7DD3FC', col:'#075985' },
                qr:     { emoji:'📱', label:'QR',  bg:'#EFF6FF', border:'#BFDBFE', col:'#1D4ED8' },
                pos:    { emoji:'🛒', label:'POS', bg:'#FEF3C7', border:'#FCD34D', col:'#92400E' },
                all:    { emoji:'🌐', label:'ALL', bg:'#F3E8FF', border:'#D8B4FE', col:'#6B21A8' },
              };
              const showAsAll = kinds.length >= 3;
              const display   = showAsAll ? ['all'] : kinds;
              return display.map(k => {
                const m = KMETA[k];
                if (!m) return null;
                return (
                  <span key={k} title={`${m.label} branch`} style={{
                    display:'inline-flex', alignItems:'center', gap:3,
                    fontSize:10, fontWeight:700,
                    color: m.col, background: m.bg,
                    border: `1px solid ${m.border}`,
                    borderRadius: 20, padding:'1px 6px',
                  }}>{m.emoji} {m.label}</span>
                );
              });
            })()}
          </div>
          {b.google_maps_url && (
            <a href={b.google_maps_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
              style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:10, color:'#16A34A', textDecoration:'none', background:'#DCFCE7', border:'1px solid #86EFAC', borderRadius:20, padding:'1px 6px' }}>
              <IcoPin /> Maps ↗
            </a>
          )}
        </div>
      </div>

      {/* 4 keys in 1 row: Phone | WhatsApp | TVs(x) | Menu */}
      <div style={{ display:'flex', gap:5, padding:'0 10px 8px' }}>
        {b.phone ? (
          <a href={`tel:${b.phone}`} title="Call" style={{ ...pill, color:'#16A34A', borderColor:'#BBF7D0' }}
            onMouseOver={e => { e.currentTarget.style.background='#DCFCE7'; e.currentTarget.style.borderColor='#86EFAC'; }}
            onMouseOut={e =>  { e.currentTarget.style.background='#FAFAFA'; e.currentTarget.style.borderColor='#BBF7D0'; }}>
            <IcoPhone />
          </a>
        ) : (
          <div title="No phone" style={{ ...pill, color:'#DDD', borderColor:'#F0F0F0', cursor:'default' }}><IcoPhone /></div>
        )}

        {b.whatsapp ? (
          <a href={`https://wa.me/${b.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
            title="WhatsApp"
            style={{ ...pill, color:'#25D366', borderColor:'#B7EBC8' }}
            onMouseOver={e => { e.currentTarget.style.background='#F0FFF4'; e.currentTarget.style.borderColor='#86EFAC'; }}
            onMouseOut={e =>  { e.currentTarget.style.background='#FAFAFA'; e.currentTarget.style.borderColor='#B7EBC8'; }}>
            <IcoWhatsApp />
          </a>
        ) : (
          <div title="No WhatsApp" style={{ ...pill, color:'#DDD', borderColor:'#F0F0F0', cursor:'default' }}><IcoWhatsApp /></div>
        )}

        <button onClick={tvsClickable ? onTVs : undefined}
          disabled={!tvsClickable}
          title={!isSignage ? 'Not a signage branch' : (totalTVs === 0 ? 'No TVs linked yet' : 'Jump to this branch\'s TVs')}
          style={{
            ...pill,
            color: tvsClickable ? '#1565C0' : '#BBB',
            borderColor: tvsClickable ? '#90CAF9' : '#EEE',
            cursor: tvsClickable ? 'pointer' : 'not-allowed',
            opacity: tvsClickable ? 1 : 0.55,
          }}
          onMouseOver={e => { if (tvsClickable) { e.currentTarget.style.background='#E3F2FD'; e.currentTarget.style.borderColor='#64B5F6'; } }}
          onMouseOut={e =>  { e.currentTarget.style.background='#FAFAFA'; e.currentTarget.style.borderColor= tvsClickable ? '#90CAF9' : '#EEE'; }}>
          <IcoTV /><span>TVs ({totalTVs})</span>
        </button>

        {onMenu && (
          <button onClick={menuClickable ? onMenu : undefined}
            disabled={!menuClickable}
            title={menuClickable ? 'Branch Menu (overrides)' : 'Brand has no menu yet — create categories/items in Menu Edit first'}
            style={{
              ...pill,
              color: menuClickable ? '#92400E' : '#BBB',
              borderColor: menuClickable ? '#FEF08A' : '#EEE',
              background: menuClickable ? '#FEFCE8' : '#FAFAFA',
              cursor: menuClickable ? 'pointer' : 'not-allowed',
              opacity: menuClickable ? 1 : 0.55,
            }}
            onMouseOver={e => { if (menuClickable) { e.currentTarget.style.background='#FEF9C3'; e.currentTarget.style.borderColor='#FACC15'; } }}
            onMouseOut={e =>  { e.currentTarget.style.background = menuClickable ? '#FEFCE8' : '#FAFAFA'; e.currentTarget.style.borderColor = menuClickable ? '#FEF08A' : '#EEE'; }}>
            🍽 <span>Menu</span>
          </button>
        )}

        {/* POS placeholder — wired up later when the POS module ships */}
        <button
          type="button"
          disabled
          title="POS — coming soon"
          style={{
            ...pill,
            color: '#BBB',
            borderColor: '#EEE',
            background: '#FAFAFA',
            cursor: 'not-allowed',
            opacity: 0.6,
          }}
        >
          💳 <span>POS</span>
        </button>
      </div>

      {/* Bottom: Edit | Copy | Delete */}
      <div style={{ display:'flex', gap:5, padding:'7px 10px', borderTop:'1px solid #F5F5F5' }}>
        {[
          { label:'Edit',   ico:<IcoEdit />,  onClick:onEdit,      border:'#E0E0E0', bg:'#FAFAFA',  col:'#444', hBg:'#E3F2FD', hBorder:'#90CAF9', hCol:'#1565C0' },
          { label:'Copy',   ico:<IcoCopy />,  onClick:onDuplicate, border:'#E0E0E0', bg:'#FAFAFA',  col:'#444', hBg:'#E8F5E9', hBorder:'#A5D6A7', hCol:'#2E7D32' },
          { label:'Delete', ico:<IcoTrash />, onClick:onDelete,    border:'#FFCDD2', bg:'#FFF5F5',  col:'#C62828', hBg:'#FFEBEE', hBorder:'#EF9A9A', hCol:'#C62828' },
        ].map(btn => (
          <button key={btn.label} onClick={btn.onClick} title={btn.label}
            style={{ flex:1, height:27, borderRadius:7, border:`1.5px solid ${btn.border}`, background:btn.bg, color:btn.col, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4, fontSize:11, transition:'all 0.15s' }}
            onMouseOver={e => { e.currentTarget.style.background=btn.hBg; e.currentTarget.style.borderColor=btn.hBorder; e.currentTarget.style.color=btn.hCol; }}
            onMouseOut={e =>  { e.currentTarget.style.background=btn.bg;  e.currentTarget.style.borderColor=btn.border;  e.currentTarget.style.color=btn.col; }}>
            {btn.ico} {btn.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Add/Edit Modal ────────────────────────────────────────────────────────────
function BranchModal({ editing, brands, defaultBrandId, onClose, onSaved }) {
  // kinds is the new array; fall back to legacy kind column when present.
  const initKinds = Array.isArray(editing?.kinds) && editing.kinds.length
    ? editing.kinds
    : (editing?.kind === 'mixed' ? ['signage','qr'] : [editing?.kind || 'signage']);

  const [form, setForm] = useState({
    brand_id: String(editing?.brand_id || defaultBrandId || brands[0]?.id || ''),
    name: editing?.name || '',
    name_ar: editing?.name_ar || '',
    phone: editing?.phone || '',
    whatsapp: editing?.whatsapp || '',
    google_maps_url: editing?.google_maps_url || '',
    kinds: initKinds,
    folder_name: editing?.folder_name || '',
  });
  const [attachedDevices, setAttachedDevices] = useState(null);
  const [kindsOpen, setKindsOpen] = useState(false);

  // Edit mode: load attached devices to warn admin before name/brand change.
  useEffect(() => {
    if (!editing?.id) return;
    api.get(`/branches/${editing.id}/attached-devices`)
      .then(r => setAttachedDevices(r.data))
      .catch(() => setAttachedDevices({ count: 0, devices: [] }));
  }, [editing?.id]);

  // Toggle a single kind in the kinds[] array
  const toggleKind = (k) => setForm(f => {
    const has = f.kinds.includes(k);
    let next = has ? f.kinds.filter(x => x !== k) : [...f.kinds, k];
    if (next.length === 0) next = ['signage']; // never empty
    return { ...f, kinds: next };
  });

  const KIND_LABEL = { signage: '📺 TV', qr: '📱 QR', pos: '🛒 POS' };
  // Folder is locked when:
  //  • Devices are attached (renaming would break their sync), OR
  //  • This is the canonical "Main" branch (folder_name empty/null) — the
  //    Main branch represents the user's root folder, which has no name.
  const isMainBranch = editing && (!editing.folder_name || editing.folder_name === '');
  const folderLocked = (attachedDevices?.count || 0) > 0 || isMainBranch;

  const submit = async e => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/branches/${editing.id}`, form);
      else         await api.post('/branches', form);
      toast.success(editing ? 'Updated!' : 'Branch created!');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{editing ? 'Edit Branch' : 'Add Branch'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          {/* Brand + Type on the same row.
              When the owner has only ONE brand the dropdown is read-only (no
              choice possible) but we still show it so Type sits beside something. */}
          <div className="input-group">
            <div style={{ display:'flex', gap:10 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <label style={{ display:'block', marginBottom:6, fontSize:13, fontWeight:500, color:'#444' }}>Brand</label>
                {brands.length > 1 ? (
                  <select className="input" value={form.brand_id}
                    onChange={e => setForm(f => ({ ...f, brand_id: e.target.value }))} required>
                    {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                ) : (
                  <input className="input" value={brands[0]?.name || ''} disabled
                    style={{ background:'#F5F5F5' }} />
                )}
              </div>
              <div style={{ flex:1, minWidth:0, position:'relative' }}>
                <label style={{ display:'block', marginBottom:6, fontSize:13, fontWeight:500, color:'#444' }}>Type</label>
                <button type="button" onClick={() => setKindsOpen(o => !o)}
                  style={{
                    width:'100%', padding:'8px 12px', borderRadius:8,
                    border:'1.5px solid #E0E0E0', background:'#fff', cursor:'pointer',
                    display:'flex', alignItems:'center', justifyContent:'space-between',
                    fontSize:13, fontWeight:600, color:'#222', minHeight: 38,
                  }}>
                  <span style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                    {form.kinds.length >= 3
                      ? <span style={{ background:'#F3E8FF', color:'#6B21A8', padding:'2px 8px', borderRadius:12, fontSize:11 }}>🌐 ALL</span>
                      : form.kinds.map(k => (
                          <span key={k} style={{ background:'#EFF6FF', color:'#1D4ED8', padding:'2px 8px', borderRadius:12, fontSize:11 }}>
                            {KIND_LABEL[k] || k}
                          </span>
                        ))
                    }
                  </span>
                  <span style={{ color:'#999' }}>{kindsOpen ? '▴' : '▾'}</span>
                </button>
                {kindsOpen && (
                  <>
                    <div style={{ position:'fixed', inset:0, zIndex:50 }} onClick={() => setKindsOpen(false)} />
                    <div style={{
                      position:'absolute', top:'100%', left:0, right:0, marginTop:4,
                      background:'#fff', border:'1.5px solid #E0E0E0', borderRadius:8,
                      boxShadow:'0 8px 24px rgba(0,0,0,0.10)', zIndex:51, padding:6,
                    }}>
                      {[
                        { v:'signage', label:'📺 TV (Signage / Menu-board)' },
                        { v:'qr',      label:'📱 QR (Customer-scan menu)' },
                        { v:'pos',     label:'🛒 POS (Cashier / orders)' },
                      ].map(opt => (
                        <label key={opt.v}
                          style={{
                            display:'flex', alignItems:'center', gap:8,
                            padding:'8px 10px', borderRadius:6, cursor:'pointer',
                            background: form.kinds.includes(opt.v) ? '#E3F2FD' : 'transparent',
                          }}
                          onMouseOver={e => { if (!form.kinds.includes(opt.v)) e.currentTarget.style.background='#F5F5F5'; }}
                          onMouseOut={e =>  { if (!form.kinds.includes(opt.v)) e.currentTarget.style.background='transparent'; }}>
                          <input type="checkbox" checked={form.kinds.includes(opt.v)}
                            onChange={() => toggleKind(opt.v)} />
                          <span style={{ fontSize:13, fontWeight:600 }}>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {editing && String(form.brand_id) !== String(editing.brand_id) && (
              <span style={{ display:'block', marginTop:6, fontSize:11, color:'#B45309', background:'#FFFBEB', border:'1px solid #FCD34D', borderRadius:6, padding:'5px 8px' }}>
                ⚠️ Moving this branch will clear its menu overrides (price/hide overrides reference items in the old brand).
              </span>
            )}
          </div>

          {/* Attached-devices warning (edit mode only) */}
          {editing && attachedDevices?.count > 0 && (
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8, fontSize: 12, color: '#92400E' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                ⚠️ {attachedDevices.count} device{attachedDevices.count === 1 ? '' : 's'} linked to this branch
              </div>
              <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                Renaming the branch (display name) is safe — devices stay linked.
                <strong> Folder path is locked</strong> to prevent breaking device sync. Detach devices first if you need to rename the folder.
              </div>
              <div style={{ marginTop: 6, fontSize: 10, color: '#78350F' }}>
                {attachedDevices.devices.slice(0, 5).map(d => d.device_name).join(', ')}
                {attachedDevices.count > 5 && ` … +${attachedDevices.count - 5} more`}
              </div>
            </div>
          )}

          {/* Name EN | Name AR side by side */}
          <div className="input-group">
            <div style={{ display:'flex', gap:10 }}>
              <div style={{ flex:1 }}>
                <label style={{ display:'block', marginBottom:6, fontSize:13, fontWeight:500, color:'#444' }}>Name (English)</label>
                <input className="input" autoFocus required value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Downtown" />
              </div>
              <div style={{ flex:1 }}>
                <label style={{ display:'block', marginBottom:6, fontSize:13, fontWeight:500, color:'#444' }}>Name (Arabic)</label>
                <input className="input arabic" value={form.name_ar}
                  onChange={e => setForm(f => ({ ...f, name_ar: e.target.value }))} placeholder="مثال: الفرع الرئيسي" />
              </div>
            </div>
          </div>

          {/* Phone | WhatsApp side by side */}
          <div className="input-group">
            <div style={{ display:'flex', gap:10 }}>
              <div style={{ flex:1 }}>
                <label style={{ display:'block', marginBottom:6, fontSize:13, fontWeight:500, color:'#444' }}>📞 Phone</label>
                <input className="input" type="tel" value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+20 10 1234 5678" />
              </div>
              <div style={{ flex:1 }}>
                <label style={{ display:'flex', alignItems:'center', gap:5, marginBottom:6, fontSize:13, fontWeight:500, color:'#444' }}>
                  <span style={{ color:'#25D366', display:'flex' }}><IcoWhatsApp /></span> WhatsApp
                </label>
                <input className="input" type="tel" value={form.whatsapp}
                  onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="+20 10 1234 5678" />
              </div>
            </div>
          </div>
          <div className="input-group">
            <label>Google Maps URL</label>
            <input className="input" value={form.google_maps_url}
              onChange={e => setForm(f => ({ ...f, google_maps_url: e.target.value }))} placeholder="https://maps.app.goo.gl/..." />
            <span style={{ fontSize:12, color:'#999' }}>Customers can tap 📍 to open your location</span>
          </div>
          {form.google_maps_url && (
            <a href={form.google_maps_url} target="_blank" rel="noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:'#2196F3', marginBottom:16, textDecoration:'none' }}>
              📍 Preview location ↗
            </a>
          )}

          {/* Branch folder path — auto-generated from name on creation; editable
              in edit mode ONLY if no devices are attached (renaming would break
              their sync). When admin edits this, the on-disk folder is moved too. */}
          <div className="input-group">
            <label>
              Folder path
              {folderLocked && (
                <span style={{ marginLeft: 6, fontSize: 10, color: '#92400E', background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 10, padding: '1px 6px' }}>
                  🔒 LOCKED — {isMainBranch ? 'Main branch (root)' : 'devices attached'}
                </span>
              )}
            </label>
            <input className="input" value={isMainBranch ? '(root — no folder)' : form.folder_name}
              disabled={folderLocked || !editing}
              placeholder={editing ? 'auto' : '(auto-generated from name on save)'}
              onChange={e => setForm(f => ({ ...f, folder_name: e.target.value }))}
              style={{ fontFamily: 'monospace', fontSize: 13, opacity: folderLocked || !editing ? 0.6 : 1 }} />
            <span style={{ fontSize: 11, color: '#999' }}>
              {isMainBranch
                ? 'Main branch represents the user root folder — has no on-disk path of its own.'
                : folderLocked
                  ? 'Detach devices first to rename the folder.'
                  : editing
                    ? 'Renaming moves the on-disk folder. Use lowercase letters, numbers, dashes only.'
                    : 'Auto-set from the branch name on creation.'}
            </span>
          </div>

          <button type="submit" className="btn btn-primary" style={{ width:'100%', justifyContent:'center', marginTop:8 }}>
            {editing ? 'Save Changes' : 'Create Branch'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Branches() {
  const { brandId, userId } = useParams();
  const navigate            = useNavigate();
  const location            = useLocation();
  const isUserMode          = !!userId && !brandId;
  const { user: authUser }  = useAuth();
  const isAdmin             = authUser?.role === 'admin';

  // username passed via router state when navigating from Users page
  const username = location.state?.username || 'User';

  const [branches, setBranches]   = useState([]);
  const [brands, setBrands]       = useState([]);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [tvBranch, setTvBranch]   = useState(null);
  const [editing, setEditing]     = useState(null);   // null = closed, false = new, object = edit
  const [showModal, setShowModal] = useState(false);
  const [devicePaths, setDevicePaths] = useState(null); // null = loading, [] = no devices

  // Fetch device paths for non-admin users (from media_files_link in DB)
  useEffect(() => {
    if (isAdmin) return;
    const token = localStorage.getItem('token');
    fetch('/api/user/file-manager-folders', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : {})
      .then(data => setDevicePaths(Array.isArray(data.devicePaths) ? data.devicePaths : []))
      .catch(() => setDevicePaths([]));
  }, [isAdmin]);

  // Filter branches for non-admin: only show branches whose folder_name is a
  // prefix-segment of at least one devicePath (e.g. "domiat" in "domiat/domiat-1")
  const visibleBranches = useMemo(() => {
    // In brand mode the API already scopes to this brand; owner sees all their branches.
    // Device-path filtering only applies in user mode (admin overview of a specific user).
    if (isAdmin || !isUserMode || devicePaths === null) return branches;
    if (devicePaths.length === 0) return branches;
    return branches.filter(b => {
      if (!b.folder_name) {
        // "Main" branch (NULL folder_name): show only when user has direct devices (1-segment path)
        return devicePaths.some(p => !p.includes('/'));
      }
      // Branch = second-to-last segment of any device path
      // e.g. 'sticks/3asher/3asher-v1' → branch = '3asher'
      //      'domiat/domiat-1'          → branch = 'domiat'
      return devicePaths.some(p => {
        const parts = p.split('/');
        return parts.length >= 2 && parts[parts.length - 2] === b.folder_name;
      });
    });
  }, [branches, isAdmin, devicePaths]);

  const load = async () => {
    setLoading(true);
    try {
      if (isUserMode) {
        // All branches for a user (admin view)
        const [bRes, brandRes] = await Promise.all([
          api.get(`/users/${userId}/branches`),
          api.get('/brands'),
        ]);
        setBranches(bRes.data);
        setBrands(brandRes.data);
      } else {
        // Branches for a specific brand + all brands for dropdown
        const [brandRes, bRes, allBrandsRes] = await Promise.all([
          api.get(`/brands/${brandId}`),
          api.get(`/branches?brand_id=${brandId}`),
          api.get('/brands'),
        ]);
        setCurrentBrand(brandRes.data);
        setBranches(bRes.data);
        setBrands(allBrandsRes.data);
      }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); load(); }, [brandId, userId]); // eslint-disable-line

  // Persist brand id so sidebar "Branches" shortcut works next time
  useEffect(() => {
    if (currentBrand?.id) localStorage.setItem('pm_last_brand', String(currentBrand.id));
  }, [currentBrand?.id]);

  const handleDelete = async (id) => {
    // Pull full inventory FIRST so the admin sees exactly what's at risk.
    let inv = null;
    try { inv = (await api.get(`/branches/${id}/inventory`)).data; } catch {}

    // Build a clear summary line per content category
    const counts = inv ? [
      inv.devices.count   > 0 ? `${inv.devices.count} device(s): ${inv.devices.list.slice(0,4).map(d=>d.device_name).join(', ')}${inv.devices.count>4?` …+${inv.devices.count-4}`:''}` : null,
      inv.screens.count   > 0 ? `${inv.screens.count} TV screen(s) with ${inv.screens.slides_total} slide(s)` : null,
      inv.qr_codes.count  > 0 ? `${inv.qr_codes.count} QR code(s)` : null,
      inv.overrides.total > 0 ? `${inv.overrides.total} menu override(s) (${inv.overrides.items} items, ${inv.overrides.variants} variants, ${inv.overrides.categories} categories)` : null,
      inv.folder.exists       ? `On-disk folder ${inv.branch.folder_name} (${inv.folder.subdirs.length} subfolder(s))` : null,
    ].filter(Boolean) : [];

    const intro = `Delete branch "${inv?.branch?.name || id}"?\n\nThis branch contains:\n${counts.length ? counts.map(c => '  • ' + c).join('\n') : '  (no linked content)'}\n`;

    if (!window.confirm(`${intro}\n\nClick OK to continue (you'll choose soft vs hard next), or Cancel to abort.`)) return;

    const hardMsg =
      `Choose deletion mode:\n\n` +
      `OK   = HARD delete\n  • Detach all devices (they keep working but lose branch link)\n  • Drop screens, slides, QR codes, overrides\n  • Quarantine the on-disk folder (recoverable from /store/_quarantine)\n\n` +
      `Cancel = SOFT delete\n  • Branch hidden, devices stay linked\n  • One-click restore later\n  • Nothing is touched on disk\n`;
    const hard = window.confirm(hardMsg);
    try {
      const url = hard ? `/branches/${id}?hard=1` : `/branches/${id}`;
      const r = await api.delete(url);
      toast.success(hard ? `Hard-deleted (${r.data?.detached_devices?.length || 0} device(s) detached)` : 'Soft-deleted (recoverable)');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const handleDuplicate = async id => {
    try { await api.post(`/branches/${id}/duplicate`); toast.success('Branch copied!'); load(); }
    catch { toast.error('Copy failed'); }
  };

  // ── Group branches by brand (user mode only) ─────────────────────────────
  const groups = isUserMode
    ? Object.values(branches.reduce((acc, br) => {
        const k = br.brand_id;
        if (!acc[k]) acc[k] = { brand_id: br.brand_id, brand_name: br.brand_name, brand_name_ar: br.brand_name_ar, brand_logo: br.brand_logo, branches: [] };
        acc[k].branches.push(br);
        return acc;
      }, {}))
    : null;

  const defaultBrandId = isUserMode
    ? (groups?.[0]?.brand_id || brands[0]?.id)
    : brandId;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="page">

      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          {isUserMode ? (
            // User mode: back arrow → close menu manager, reopen AdminPanel
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <button onClick={() => window.dispatchEvent(new CustomEvent('pm:back'))}
                style={{ width:36, height:36, borderRadius:10, border:'1.5px solid #E0E0E0', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, color:'#555' }}>←</button>
              <div>
                <h1 className="page-title" style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:22 }}>📍</span> Branches
                  <span style={{ fontWeight:400, color:'#999', fontSize:15 }}>({visibleBranches.length})</span>
                  {/* Brand dropdown beside the title */}
                  {brands.length > 1 && (
                    <select
                      value=""
                      onChange={e => { if (e.target.value) navigate(`/brands/${e.target.value}/branches`); }}
                      style={{ marginLeft:6, fontWeight:600, fontSize:14, color:'#1D4ED8', border:'1.5px solid #BFDBFE', borderRadius:8, background:'#EFF6FF', cursor:'pointer', outline:'none', padding:'3px 8px' }}>
                      <option value="" disabled>Switch brand…</option>
                      {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  )}
                </h1>
              </div>
            </div>
          ) : (
            // Brand mode: BrandNav + brand dropdown in title
            <>
              <BrandNav brandId={brandId} subPath="branches" backLabel="Brands" backPath="/" backKey="/" />
              <h1 className="page-title" style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <span style={{ fontWeight:700, fontSize:18, color:'#111' }}>{currentBrand?.name}</span>
                <span style={{ fontWeight:500, color:'#6B7280', fontSize:14, background:'#F3F4F6', borderRadius:8, padding:'2px 8px' }}>
                  {visibleBranches.length} branches
                </span>
              </h1>
            </>
          )}
        </div>
        {isAdmin && <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>+ Add Branch</button>}
      </div>

      {/* ── Content ── */}
      {loading ? <div className="spinner" /> : visibleBranches.length === 0 ? (
        <div className="empty-state">
          <h3>No branches yet</h3>
          <p>Add your first branch</p>
        </div>
      ) : isUserMode ? (
        // ── User mode: grouped by brand ──────────────────────────────────────
        <div style={{ display:'flex', flexDirection:'column', gap:28 }}>
          {groups.map(g => (
            <div key={g.brand_id}>
              <div onClick={() => navigate(`/brands/${g.brand_id}/branches`)}
                style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, cursor:'pointer', padding:'6px 10px 6px 6px', borderRadius:10, transition:'background 0.15s', width:'fit-content' }}
                onMouseOver={e => e.currentTarget.style.background='#EFF6FF'}
                onMouseOut={e => e.currentTarget.style.background='transparent'}>
                {g.brand_logo
                  ? <img src={g.brand_logo} alt={g.brand_name} style={{ width:32, height:32, borderRadius:8, objectFit:'contain', background:'#f5f5f5', border:'1px solid #eee' }} />
                  : <div style={{ width:32, height:32, borderRadius:8, background:'#2563EB', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:14 }}>{g.brand_name?.charAt(0)}</div>
                }
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:'#1D4ED8' }}>{g.brand_name}</div>
                  {g.brand_name_ar && <div style={{ fontSize:12, color:'#999', fontFamily:'Cairo' }}>{g.brand_name_ar}</div>}
                </div>
                <span style={{ fontSize:11, background:'#BFDBFE', color:'#1D4ED8', borderRadius:20, padding:'2px 8px', fontWeight:700, marginLeft:4 }}>{g.branches.length}</span>
                <span style={{ color:'#93C5FD', fontSize:14 }}>›</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:12 }}>
                {g.branches.map(br => (
                  <BranchCard key={br.id} b={br}
                    onEdit={e => { e.stopPropagation(); setEditing(br); setShowModal(true); }}
                    onDuplicate={e => { e.stopPropagation(); handleDuplicate(br.id); }}
                    onDelete={e => { e.stopPropagation(); handleDelete(br.id); }}
                    onMenu={e => { e.stopPropagation(); navigate(`/brands/${br.brand_id}/branches/${br.id}/menu`); }}
                    onTVs={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("pm:nav", { detail: { kind: "devices", username: username, branchId: br.id, branchFolder: br.folder_name } })); }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // ── Brand mode: flat grid ────────────────────────────────────────────
        <div className="grid-3">
          {visibleBranches.map(b => (
            <BranchCard key={b.id} b={b}
              onEdit={e => { e.stopPropagation(); setEditing(b); setShowModal(true); }}
              onDuplicate={e => { e.stopPropagation(); handleDuplicate(b.id); }}
              onDelete={e => { e.stopPropagation(); handleDelete(b.id); }}
              onMenu={e => { e.stopPropagation(); navigate(`/brands/${b.brand_id}/branches/${b.id}/menu`); }}
              onTVs={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("pm:nav", { detail: { kind: "devices", username: b.username || "", branchId: b.id, branchFolder: b.folder_name } })); }} />
          ))}
        </div>
      )}

      {/* ── TV Modal ── */}
      {tvBranch && <TVModal branch={tvBranch} onClose={() => { setTvBranch(null); load(); }} />}

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <BranchModal
          editing={editing}
          brands={brands}
          defaultBrandId={defaultBrandId}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
